import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { WidgetBox } from '../components/dashboard/dashboardWidgets';

interface DashboardLayoutStore {
  orderByCollaborator: Record<string, string[]>;
  spansByCollaborator: Record<string, Record<string, number>>;
  heightsByCollaborator: Record<string, Record<string, number>>;
  /** Place de chaque bloc dans la grille. Remplace ordre, largeur et hauteur ci-dessus (anciens réglages, lus une fois). */
  boxesByCollaborator: Record<string, Record<string, WidgetBox>>;
  setOrder: (collaboratorId: string, keys: string[]) => void;
  setSpan: (collaboratorId: string, key: string, span: number) => void;
  setHeight: (collaboratorId: string, key: string, height: number) => void;
  setBoxes: (collaboratorId: string, boxes: Record<string, WidgetBox>) => void;
  resetOrder: (collaboratorId: string) => void;
}

export const useDashboardLayoutStore = create<DashboardLayoutStore>()(
  persist(
    (set) => ({
      orderByCollaborator: {},
      spansByCollaborator: {},
      heightsByCollaborator: {},
      boxesByCollaborator: {},

      setOrder: (collaboratorId, keys) =>
        set((state) => ({
          orderByCollaborator: {
            ...state.orderByCollaborator,
            [collaboratorId]: keys,
          },
        })),

      setSpan: (collaboratorId, key, span) =>
        set((state) => ({
          spansByCollaborator: {
            ...state.spansByCollaborator,
            [collaboratorId]: {
              ...state.spansByCollaborator[collaboratorId],
              [key]: span,
            },
          },
        })),

      setHeight: (collaboratorId, key, height) =>
        set((state) => ({
          heightsByCollaborator: {
            ...state.heightsByCollaborator,
            [collaboratorId]: {
              ...state.heightsByCollaborator[collaboratorId],
              [key]: height,
            },
          },
        })),

      setBoxes: (collaboratorId, boxes) =>
        set((state) => ({
          boxesByCollaborator: {
            ...state.boxesByCollaborator,
            [collaboratorId]: {
              ...state.boxesByCollaborator[collaboratorId],
              ...boxes,
            },
          },
        })),

      resetOrder: (collaboratorId) =>
        set((state) => {
          const nextOrder = { ...state.orderByCollaborator };
          const nextSpans = { ...state.spansByCollaborator };
          const nextHeights = { ...state.heightsByCollaborator };
          const nextBoxes = { ...state.boxesByCollaborator };
          delete nextOrder[collaboratorId];
          delete nextSpans[collaboratorId];
          delete nextHeights[collaboratorId];
          delete nextBoxes[collaboratorId];
          return {
            orderByCollaborator: nextOrder,
            spansByCollaborator: nextSpans,
            heightsByCollaborator: nextHeights,
            boxesByCollaborator: nextBoxes,
          };
        }),
    }),
    {
      name: 'cime.dashboard.layout',
      partialize: (state) => ({
        orderByCollaborator: state.orderByCollaborator,
        spansByCollaborator: state.spansByCollaborator,
        heightsByCollaborator: state.heightsByCollaborator,
        boxesByCollaborator: state.boxesByCollaborator,
      }),
      merge: (persisted, current) => {
        const p = persisted as Partial<DashboardLayoutStore> | undefined;
        return {
          ...current,
          ...p,
          orderByCollaborator: p?.orderByCollaborator ?? current.orderByCollaborator,
          spansByCollaborator: p?.spansByCollaborator ?? {},
          heightsByCollaborator: p?.heightsByCollaborator ?? {},
          boxesByCollaborator: p?.boxesByCollaborator ?? {},
        };
      },
    },
  ),
);
